/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {Injectable, inject} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {exhaustMap, map, tap} from 'rxjs';
import {AppActions} from '@shared/common-core/store/actions';
import {IamUser} from '@shared/modules/auth/models/iam-user';
import {AuthService} from '@shared/modules/auth/services/auth.service';
import {LocalStorageService} from '@common-services/local-storage/local-storage.service';

/**
 * This file contains the effects for the app module
 */
@Injectable()
export class AppEffects {
  private actions$ = inject(Actions);
  private localStorageService = inject(LocalStorageService);
  private authService = inject(AuthService);

  loadJiraModalDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.loadJiraModalDetails),
      exhaustMap(() => {
        return this.authService
          .getUserProfile(this.localStorageService.getItem('pspUserId'))
          .pipe(map((userDetails: IamUser) => AppActions.openJiraModal({userDetails})));
      })
    );
  });

  openJiraModal$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AppActions.openJiraModal),
        tap(({userDetails}) => {
          const iframeContent: Document = (
            document.querySelector('#jsd-widget') as HTMLIFrameElement
          )?.contentDocument as Document;
          if (iframeContent) {
            const button = iframeContent.querySelector(
              '#help-button'
            ) as HTMLButtonElement | null;
            button?.click();

            // FIXME: This is a hack to wait for the iframe to load before we can interact with it
            // Not sure if there is a better way to do this
            // but it is forcing the reload of the iframe content
            // and then the helpForm has content
            //
            // In addition, this hack is fixing the issue of the email input not being pre-filled
            //
            setTimeout(() => {
              if (iframeContent) {
                const helpForm = iframeContent.querySelector(
                  '.help-form'
                ) as HTMLElement | null;
                if (helpForm) {
                  // set the height of the help form
                  helpForm.style.maxHeight = '510px';
                  helpForm.style.height = '510px';

                  // the Spanish translation of the priority labels
                  const priorityLabels = {
                    Highest: 'Muy Alta',
                    High: 'Alta',
                    Medium: 'Media',
                    Low: 'Baja',
                    Lowest: 'Muy Baja',
                  };

                  // get the Priority select element
                  const helpFormPrioritySelect = helpForm?.children[0]
                    ?.children[1] as HTMLElement | null;

                  // get the selected option label element
                  const helpFormPrioritySelectedOption = helpFormPrioritySelect?.children[1]
                    ?.children[2]?.children[0]?.children[0] as HTMLElement | null;

                  // set the selected option label
                  if (helpFormPrioritySelectedOption) {
                    helpFormPrioritySelectedOption.textContent =
                      priorityLabels[
                        helpFormPrioritySelectedOption.textContent as keyof typeof priorityLabels
                      ];
                  }

                  // select the priority select element
                  const priorityElement = helpForm?.querySelector(
                    '#priority'
                  ) as HTMLElement | null;

                  // define the observer configuration
                  const config = {attributes: true, childList: false, subtree: false};

                  // create a new observer instance to observe the priority input element changes
                  const priorityObserver = new MutationObserver(() => {
                    if (priorityElement?.getAttribute('aria-expanded') === 'false') {
                      // set the selected option label
                      if (helpFormPrioritySelectedOption) {
                        const value = helpFormPrioritySelectedOption.textContent;
                        const newValue = priorityLabels[value as keyof typeof priorityLabels];

                        // set the selected option label only if the value is found
                        if (newValue) {
                          helpFormPrioritySelectedOption.textContent = newValue;
                        }
                      }
                    }
                  });
                  if (priorityElement) {
                    priorityObserver.observe(priorityElement, config);
                  }

                  // set an event listener to the priority select element
                  helpFormPrioritySelect?.addEventListener('click', () => {
                    const selectOptions = helpFormPrioritySelect?.children[1]?.children[3]
                      ?.children[0] as HTMLElement | null;

                    selectOptions?.childNodes.forEach(selectOption => {
                      selectOption.textContent =
                        priorityLabels[
                          selectOption.textContent as keyof typeof priorityLabels
                        ];
                    });
                  });

                  const helpFormUpload = helpForm?.children[0]
                    ?.children[3] as HTMLElement | null;

                  // get the the p tag in the help form upload section
                  const helpFormUploadHelpMessage = helpFormUpload?.querySelector(
                    'p'
                  ) as HTMLElement | null;

                  // set the help message
                  if (helpFormUploadHelpMessage) {
                    helpFormUploadHelpMessage.textContent =
                      'El tamaño del archivo no debe exceder los 5 MB';
                  }
                  // get the span tag in the drop zone
                  const helpFormUploadDropZone = helpFormUpload?.children[2]?.children[1]
                    ?.children[1]?.children[0] as HTMLElement | null;

                  // get the button in the drop zone
                  const helpFormUploadDropZoneButton = helpFormUploadDropZone
                    ?.childNodes[1] as HTMLElement | null;

                  // set the drop zone message
                  if (helpFormUploadDropZone) {
                    helpFormUploadDropZone.childNodes[0].textContent =
                      'Suelte archivos para adjuntarlos o ';
                  }

                  // set the drop zone button text
                  if (helpFormUploadDropZoneButton) {
                    helpFormUploadDropZoneButton.textContent = 'seleccione';
                  }

                  // get the email input element
                  const helpFormEmailInputLabel = helpForm?.children[0]?.children[4]
                    ?.children[0] as HTMLElement | null;

                  // set the email input label
                  if (helpFormEmailInputLabel) {
                    helpFormEmailInputLabel.textContent = 'Su correo electrónico de contacto';
                  }

                  // get the submit button
                  const helpFormSubmitButton = helpForm?.querySelector(
                    '#submit-button'
                  ) as HTMLElement | null;

                  // set the submit button text
                  if (helpFormSubmitButton) {
                    helpFormSubmitButton.children[0].textContent = 'Enviar';
                  }

                  const emailInput = iframeContent.querySelector(
                    '#email'
                  ) as HTMLInputElement | null;
                  if (emailInput) {
                    // Hack to pre-fill the value of an input element in React (the widget framework) https://stackoverflow.com/a/48197567
                    const prototype = Object.getPrototypeOf(emailInput);
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                      prototype,
                      'value'
                    )?.set;
                    // Set the value of the input
                    nativeInputValueSetter?.call(emailInput, userDetails?.email ?? '');
                    // Notify the widget that the input had been changed (onChange event in their library)
                    emailInput.dispatchEvent(new Event('input', {bubbles: true}));
                  }
                }
              }
            }, 1);

            const financialEntityNameContainer = iframeContent
              .querySelector('#customfield_10074')
              ?.closest('div') as HTMLElement | null;
            const optionalLabel = financialEntityNameContainer?.querySelector('.optional');
            const financialEntityNameInput =
              financialEntityNameContainer?.querySelector('input');
            financialEntityNameContainer?.setAttribute('hidden', '');
            optionalLabel?.remove();

            // Will dismiss the toast notification after 10 seconds of form submitted
            const submitButton = iframeContent.querySelector(
              '#submit-button'
            ) as HTMLButtonElement | null;
            submitButton?.addEventListener('click', () => {
              setTimeout(() => {
                const notificationMessage: HTMLElement | null = iframeContent.querySelector(
                  '#confirmation-container .close-icon'
                );
                notificationMessage?.click();
              }, 10000);
            });
          }
        })
      );
    },
    {dispatch: false}
  );

  closeJiraModal$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AppActions.closeJiraModal),
        tap(() => {
          const iframeContent: Document = (
            document.querySelector('#jsd-widget') as HTMLIFrameElement
          )?.contentDocument as Document;

          if (iframeContent) {
            const notificationMessage: HTMLElement | null =
              iframeContent.querySelector('.close-icon');
            notificationMessage?.click();
          }
        })
      );
    },
    {dispatch: false}
  );
}
